import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Card, CardContent, CardMedia, Chip, styled, Tooltip, Typography, useTheme } from '@mui/material';
import { Help, LocationOn, Stars } from '@mui/icons-material';

import { emptyState } from '@frontend/assets';
import { amenities as amenitiesIcons } from '@frontend/assets/amenities';
import { useBreakpoints } from '@frontend/features/common/hooks/useBreakpoints';
import { toProperName } from '@frontend/features/common/helper';
import Rating from '@frontend/features/hotel-content/components/Detail/Rating';

import { HotelAmentityTextDTO, HotelAvailabilityEmit } from '@bandido/shared/dtos';

import { getAmenityTooltipText } from '../../helpers';

interface DetailCardProps {
  hotel: HotelAvailabilityEmit;
  isCompact?: boolean;
  footer?: React.ReactNode;
}

const StyledCard = styled(
  Card, {
    shouldForwardProp: (prop) => prop !== 'shouldShowCompact',
  }
)<{ shouldShowCompact: boolean }>(({ shouldShowCompact }) => ({
  display: 'flex',
  position: 'relative',
  width: '100%',
  maxWidth: '840px',
  flexDirection : !shouldShowCompact ? 'row' : 'column',
}));

const StyledCardMedia = styled(
  CardMedia, {
    shouldForwardProp: (prop) => prop !== 'shouldShowCompact',
  }
)<{ shouldShowCompact: boolean }>(({ shouldShowCompact }) => ({
  ...(!shouldShowCompact ?
    {
      width: 380,
      minWidth: 380,
      height: 280,
      minHeight: 280
    } :
    {
      width: '100%',
      height: 280,
      minHeight: 280
    }),
  overflow: 'hidden',
  objectFit: 'cover',
}));

const StyledDetailChip = styled(
  Chip,
  {
    shouldForwardProp: (prop) => prop !== 'isAmenity',
  }
)<{ isAmenity?: boolean }>(({ theme, isAmenity }) => ({
  backgroundColor: isAmenity ? theme.palette.paper.background : 'auto',
  width: 32,
  height: 32,
  '&& svg.MuiChip-avatar, && svg.MuiChip-avatar *': {
    fill: isAmenity ? theme.palette.disabled.main : theme.palette.disabled.light,
    color: isAmenity ? theme.palette.disabled.main: theme.palette.disabled.light,
    margin: 0,
    minWidth: 24,
    minHeight: 24,
    left: 12,
    position: 'relative',
  }
}));

const StyledCardContent = styled(CardContent)(() => ({
  flex: 1,
  flexDirection: 'column',
  display: 'flex',
  justifyContent: 'space-between',
  overflow: 'hidden',
  width:'auto',
  maxWidth:'100%',
  '&&': {
    padding: 0,
  },
}));

export const DetailCard: FC<DetailCardProps> = ({ hotel, footer, isCompact }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { isMediumDown } = useBreakpoints();

  const getHotelImg = useCallback(() => {
    if (hotel.picture) {
      return hotel.picture;
    }
    return emptyState;
  }, [hotel]);

  const getAmenityTooltip = (
    systemMappingKey: string,
    texts: HotelAmentityTextDTO[],
    code: string
  ) => {
    const textToShow = getAmenityTooltipText(systemMappingKey, texts, code);
    if (textToShow.translated) {
      return t(textToShow.text);
    } else {
      return textToShow.text;
    }
  };

  const shouldShowCompact = useMemo(() => isCompact || isMediumDown, [isCompact, isMediumDown]);


  return (
    <StyledCard shouldShowCompact={shouldShowCompact}>
      <StyledCardMedia image={getHotelImg()} shouldShowCompact={shouldShowCompact} />
      <StyledCardContent>
        { 
          <>
            <Box
              gap={2}
              padding={theme.spacing(2)}
              display="flex"
              flexDirection="column"
            >
              <Typography
                variant="subtitle1"
                color={theme.palette.text.primary}
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}
              >
                {hotel.name ? toProperName(hotel.name) : ''}
              </Typography>
              <Box display="flex" flexDirection="column">
                <Rating rating={hotel.rating} totalReviews={hotel.totalReviews} />
                <Box display="flex" alignItems="center">
                  <StyledDetailChip
                    avatar={(<Stars />)}
                    size="medium"
                    sx={{ backgroundColor: 'transparent' }} />
                  <Typography variant="caption" color={theme.palette.text.secondary}>
                    {/* FIXME: hay q traer las categories tambien */}
                    {/* <DescriptionEditorCategory
                      categoryId={hotel.categoryId}
                      lang={InfoLanguage.English}
                      onlyText /> */}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <StyledDetailChip
                    avatar={(<LocationOn />)}
                    size="medium"
                    sx={{
                      backgroundColor: 'transparent'
                    }} />
                  <Typography variant="caption" color={theme.palette.text.secondary} noWrap>
                    {`${hotel.address}, ${hotel.city}, ${hotel.country}`}
                  </Typography>
                </Box>
              </Box>
              <Box
                display="flex"
                flexWrap="nowrap"
                maxWidth="100%"
                gap={2}
              >
                {/* 
                FIXME: tengo q arreglar el tipado de amenities
                // @ts-ignore */}
                {hotel.amenities?.slice(0, 5).map(({ id, code, texts, systemMappingKey }) => (
                  <Tooltip key={id} title={getAmenityTooltip(systemMappingKey || '', texts, code)}>
                    <StyledDetailChip
                      isAmenity
                      key={id}
                      avatar={systemMappingKey ? amenitiesIcons[systemMappingKey]() : <Help />}
                      size="medium" />
                  </Tooltip>
                ))}
              </Box>
              {/*<Box display="flex">
                // TODO: evaluar con los datos a futuro si lo podemos tener.
                <Box>
                  <Chip
                    avatar={
                      <Check style={{ color: theme.palette.success.main }} />
                    }
                    size="small"
                    sx={{ width: 24, backgroundColor: 'transparent' }}
                  />
                  <Typography variant="caption" color={theme.palette.text.secondary}>
                    {t('hotels.cancelationPolicies.free')}
                  </Typography>
                </Box>
                <Box>
                  <Chip
                    avatar={
                      <Check style={{ color: theme.palette.success.main }} />
                    }
                    size="small"
                    sx={{ backgroundColor: 'transparent' }}
                  />
                  <Typography variant="caption" color={theme.palette.text.secondary}>
                    {t('hotels.paymentPolicies.nonAnticipate')}
                  </Typography>
                </Box>
              
              </Box>*/}
            </Box><Box
              padding={theme.spacing(2)}
              borderTop={`0.5px solid ${theme.palette.disabled.light}`}
            >
              {footer}
            </Box>
          </>
        }
      </StyledCardContent>
    </StyledCard>
  );
};
